<template>
  <div>
    <v-container class="px-0">
      <v-row no-gutters>
        <v-col cols="12" class="px-2">
          <v-card class="mx-auto" outlined>
            <v-list-item three-line>
              <v-list-item-content>
                <div class="overline mb-4">
                  {{ params.timestamp | moment("from") }}
                </div>
                <v-list-item-title class="headline mb-1">
                  Parameters
                  <v-chip
                    class="ma-2"
                    color="red"
                    text-color="white"
                    v-if="params.updatedBy == 'user'"
                  >
                    Updated by user
                  </v-chip>
                  <template v-else>
                    <v-chip class="ma-2">
                      Updated by {{ params.updatedBy }}
                    </v-chip>
                    <v-chip
                      class="ma-2"
                      color="primary"
                      text-color="white"
                      v-if="params.acknowledged"
                    >
                      <v-icon left>mdi-check</v-icon>
                      Acknowledged
                    </v-chip>
                    <v-chip class="ma-2" v-else>
                      <v-icon left>mdi-timer-sand-empty</v-icon>
                      Not acknowledged
                    </v-chip>
                  </template>
                </v-list-item-title>
              </v-list-item-content>

              <v-list-item-action
                v-if="
                  userRole === 'admin' || userRole === 'rn' || userRole === 'md'
                "
              >
                <v-btn
                  class="ma-2"
                  outlined
                  small
                  fab
                  color="primary"
                  @click="isEditingParams = !isEditingParams"
                >
                  <v-icon>{{
                    isEditingParams ? "mdi-close" : "mdi-pencil"
                  }}</v-icon>
                </v-btn>
              </v-list-item-action>
            </v-list-item>

            <transition name="slide-fade" mode="out-in">
              <ParametersForm
                v-show="isEditingParams"
                class="fill-space"
                @saved="isEditingParams = false"
              />
            </transition>

            <md-table v-model="paramsArray">
              <md-table-row
                slot="md-table-row"
                slot-scope="{ item, index }"
                :class="{
                  'current-params-row': item.id === study.currentParamsID
                }"
              >
                <md-table-cell md-label="" md-sort-by="timestamp" md-numeric>
                  <div class="d-flex flex-row justify-end align-center">
                    <md-button
                      class="md-icon-button notes-icon"
                      @click="showAllParameters(item.id)"
                    >
                      <md-icon>visibility</md-icon>
                    </md-button>
                    <div>
                      <div v-if="item.id === study.currentParamsID">
                        <b>Current Params</b>
                      </div>
                      {{ formatDate(item.timestamp) }}
                    </div>
                  </div>
                </md-table-cell>
                <md-table-cell
                  md-label="Basal dose"
                  md-sort-by="basaDose"
                  :class="{
                    'item-has-changed': itemHasChanged(item, index, 'basaDose')
                  }"
                  >{{ item.basaDose }}
                  <params-override-tooltip
                    :algoValue="findCorrespondingAlgoParam(item, 'basaDose')"
                  />
                </md-table-cell>
                <md-table-cell
                  md-label="Breakfast"
                  :class="{
                    'item-has-changed': itemHasChanged(item, index, 'breakfast')
                  }"
                  >{{
                    item.isAM
                      ? item.insuCarbRatio.breakfast
                      : item.insuDose.breakfast
                  }}
                  <params-override-tooltip
                    :algoValue="findCorrespondingAlgoParam(item, 'breakfast')"
                  />
                </md-table-cell>
                <md-table-cell
                  md-label="Lunch"
                  :class="{
                    'item-has-changed': itemHasChanged(item, index, 'lunch')
                  }"
                  >{{
                    item.isAM ? item.insuCarbRatio.lunch : item.insuDose.lunch
                  }}
                  <params-override-tooltip
                    :algoValue="findCorrespondingAlgoParam(item, 'lunch')"
                  />
                </md-table-cell>
                <md-table-cell
                  md-label="Dinner"
                  :class="{
                    'item-has-changed': itemHasChanged(item, index, 'supper')
                  }"
                  >{{
                    item.isAM ? item.insuCarbRatio.supper : item.insuDose.supper
                  }}
                  <params-override-tooltip
                    :algoValue="findCorrespondingAlgoParam(item, 'supper')"
                  />
                </md-table-cell>
                <md-table-cell
                  md-label="Bedtime"
                  :class="{
                    'item-has-changed': itemHasChanged(item, index, 'bedtime')
                  }"
                  >{{
                    item.isAM
                      ? item.insuCarbRatio.bedtime
                      : item.insuDose.bedtime
                  }}
                  <params-override-tooltip
                    :algoValue="findCorrespondingAlgoParam(item, 'bedtime')"
                  />
                </md-table-cell>
                <md-table-cell md-label="Admin" md-sort-by="adminUserId">
                  <template v-if="item.updatedBy == 'user'">
                    <md-icon class="md-accent acknowledged-icon"
                      >edit<md-tooltip md-direction="left"
                        >Parameters updated by user</md-tooltip
                      ></md-icon
                    >
                  </template>
                  <template v-else>
                    <md-icon
                      class="md-primary acknowledged-icon"
                      v-if="item.acknowledged"
                      >check<md-tooltip md-direction="left"
                        >Parameters acknowledged</md-tooltip
                      ></md-icon
                    >
                    <md-icon class="acknowledged-icon" v-else
                      >hourglass_empty<md-tooltip md-direction="left"
                        >Parameters not acknowledged yet</md-tooltip
                      ></md-icon
                    >
                  </template>

                  {{ translateUpdatedBy(item.updatedBy, item.adminUserId) }}

                  <md-button
                    class="md-icon-button md-raised notes-icon md-primary"
                    @click="showNotes(item.privateNotes)"
                    v-show="item.privateNotes"
                  >
                    <md-icon>notes</md-icon>
                  </md-button>
                  <md-button
                    class="md-icon-button md-raised notes-icon md-primary"
                    @click="showNotes(item.updateReason)"
                    v-show="item.updateReason"
                  >
                    <md-icon>notes</md-icon>
                  </md-button>
                </md-table-cell>
              </md-table-row>
            </md-table>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <md-dialog-alert
      :md-active.sync="notesModalVisible"
      md-title="Private note"
      :md-content="notesModalText"
    />

    <v-dialog v-model="parametersModalVisible" max-width="500px">
      <v-card>
        <v-card-title>
          <span class="headline">Parameters</span>
        </v-card-title>
        <v-card-text>
          <div
            v-for="(value, key) in paramsArray.find(
              p => p.id === parametersModalID
            )"
            :key="key"
            v-show="parametersKeyToName[key]"
          >
            <div class="d-flex flex-row justify-space-between my-2">
              <v-chip>{{ parametersKeyToName[key] }}</v-chip>
              <div
                class="black--text font-weight-bold"
                v-show="
                  key !== 'exerRatios' &&
                    key !== 'fatRatios' &&
                    key !== 'insuCarbRatio'
                "
              >
                {{ value }}
              </div>
            </div>
            <v-data-table
              v-if="key === 'exerRatios'"
              :headers="parameterExerRatiosHeaders"
              :items="value"
              disable-sort
              hide-default-footer
            ></v-data-table>
            <v-data-table
              v-else-if="key === 'fatRatios'"
              :headers="parameterFatRatiosHeaders"
              :items="generateFatRatiosTable(value)"
              disable-sort
              hide-default-footer
            ></v-data-table>
            <v-data-table
              v-else-if="key === 'insuCarbRatio'"
              :headers="parameterInsuCarbRatioHeaders"
              :items="[value]"
              disable-sort
              hide-default-footer
            ></v-data-table>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="blue darken-1"
            text
            @click="parametersModalVisible = false"
            >OK</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped>
.updated-by-chip {
  margin-left: 10px;
}
.acknowledged-chip {
  margin-left: 10px;
}
.acknowledged-icon {
  margin-right: 10px;
}
.md-icon-button.notes-icon {
  width: 25px;
  min-width: 25px;
  height: 25px;
  margin-left: 10px;
}
.notes-icon .md-icon {
  transform: scale(0.75);
}
.icon-not-carbs {
  color: rgba(0, 0, 0, 0.15) !important;
}

.item-has-changed {
  background: rgba(0, 0, 0, 0.1);
  font-weight: bold;
  font-size: 14px;
}
</style>

<script>
import moment from "moment";
import { EventBus } from "../main";
import ParametersForm from "../components/ParametersForm";
import ParamsOverrideTooltip from "../components/ParamsOverrideTooltip";
import { parametersKeyToName } from "../globals";

import { mapState } from "vuex";

export default {
  name: "parameters-history-card",
  components: {
    ParametersForm,
    ParamsOverrideTooltip
  },
  props: ["paramsArray", "params", "study"],
  data: () => ({
    notesModalVisible: false,
    notesModalText: null,
    adminUsers: EventBus.adminUsers,
    isEditingParams: false,
    parametersModalID: null,
    parametersModalVisible: false,
    parametersKeyToName: parametersKeyToName,
    parameterExerRatiosHeaders: [
      { text: "Type", value: "type" },
      { text: "B <", value: "bre_l" },
      { text: "B >", value: "bre_m" },
      { text: "L <", value: "lun_l" },
      { text: "L >", value: "lun_m" },
      { text: "D <", value: "sup_l" },
      { text: "D >", value: "sup_m" }
    ],
    parameterFatRatiosHeaders: [
      { text: "Mealtime", value: "meal" },
      { text: "Low (%)", value: "l" },
      { text: "High (%)", value: "h" }
    ],
    parameterInsuCarbRatioHeaders: [
      { text: "Breakfast", value: "breakfast" },
      { text: "Lunch", value: "lunch" },
      { text: "Dinner", value: "supper" },
      { text: "Bedtime", value: "bedtime" }
    ]
  }),
  computed: {
    ...mapState(["dbAdminUsers", "userRole", "currentParamsApproved"]),
    algoPrefix: function() {
      return this.params.isAM ? "cr" : "dose";
    },
    dosePrefix: function() {
      return this.params.isAM ? "insuCarbRatio" : "insuDose";
    }
  },
  methods: {
    fromNow(timestamp) {
      return moment(timestamp).fromNow();
    },
    formatDate(timestamp) {
      return moment(timestamp).format("MMM DD, HH:mm");
    },
    showNotes(notes) {
      this.notesModalText = notes;
      this.notesModalVisible = true;
    },
    showAllParameters(id) {
      this.parametersModalID = id;
      this.parametersModalVisible = true;
    },
    generateFatRatiosTable(obj) {
      return [
        {
          meal: "Breakfast",
          l: obj.bre_l,
          h: obj.bre_h
        },
        {
          meal: "Lunch",
          l: obj.lun_l,
          h: obj.lun_h
        },
        {
          meal: "Dinner",
          l: obj.sup_l,
          h: obj.sup_h
        }
      ];
    },
    translateUpdatedBy(updatedBy, adminId) {
      if (updatedBy === "user") {
        return "Updated by participant";
      } else if (updatedBy === "algo") {
        return "Algorithm";
      } else if (adminId) {
        let admin = this.dbAdminUsers[adminId];
        if (admin) {
          let role = "ADMIN";
          if (admin.role == "md") {
            role = "MD";
          } else if (admin.role == "rn") {
            role = "RN";
          }

          let action = "update";
          if (updatedBy === "algo-admin-override") {
            action = "override";
          } else if (updatedBy === "algo-admin-approved") {
            action = "approval";
          }

          return `${role} ${action} (${admin.name})`;
        } else {
          return "[Deleted user]";
        }
      } else {
        return "N/A";
      }
    },
    itemHasChanged(item, index, key) {
      if (index < this.paramsArray.length - 1) {
        let { isAM } = item;
        let previousItem = this.paramsArray[index + 1];

        if (key === "basaDose") {
          if (item.basaDose !== previousItem.basaDose) {
            return true;
          }
        } else if (isAM !== previousItem.isAM) {
          return true;
        } else if (isAM) {
          if (item.insuCarbRatio[key] !== previousItem.insuCarbRatio[key]) {
            return true;
          }
        } else {
          if (item.insuDose[key] !== previousItem.insuDose[key]) {
            return true;
          }
        }
      }

      return false;
    },
    findCorrespondingAlgoParam(params, key) {
      if (
        this.currentParamsApproved &&
        params.id in this.currentParamsApproved
      ) {
        const oldParams = this.currentParamsApproved[params.id];

        let curValue;
        let algoValue;

        if (key == "basaDose") {
          curValue = params["basaDose"];
          algoValue = oldParams["basalDose"];
        } else {
          const algoKey = {
            breakfast: "Breakfast",
            lunch: "Lunch",
            supper: "Dinner",
            bedtime: "Bedtime"
          };
          curValue = params[this.dosePrefix][key];
          algoValue = oldParams[`${this.algoPrefix}${algoKey[key]}`];
        }

        if (algoValue != curValue) {
          return algoValue;
        }
      }

      return null;
    }
  }
};
</script>
